import type {
  AlignmentPlan,
  AnalysisDetail,
  AnalysisRow,
  Decision,
  ReceivableDraft,
  VersionDetail
} from "../api/types.ts"
import type { WorkspaceTab } from "../workspace/WorkspaceContext.tsx"

export const WIZARD_STEPS = ["destination", "plan", "review"] as const
export type WizardStep = (typeof WIZARD_STEPS)[number]
export type WizardPanel = "template" | "log"

/** Where the user said the plan goes. "new_*" exist only until the record is created. */
export type Destination =
  | "project"
  | "lead"
  | "new_project"
  | "new_lead"
  | "unlinked"

export const stepNumber = (step: WizardStep) => WIZARD_STEPS.indexOf(step) + 1

// A draft remembered from when scope, schedule and payments were separate steps.
export const knownStep = (step: string | undefined): WizardStep | undefined =>
  step === "scope" || step === "schedule" || step === "payments"
    ? "plan"
    : (step as WizardStep | undefined)

const DEPOSIT_WORDS = /\b(deposit|down[\s-]?payment|retainer)\b/i

/** The analysis has a reviewable result: steps 2 and 3 have something to show. */
export function hasResult(analysis: AnalysisDetail | AnalysisRow): boolean {
  const stage = analysis.current_version?.stage
  return (
    analysis.status !== "analyzing" &&
    stage !== "failed" &&
    stage !== "cancelled" &&
    stage !== "cancelling" &&
    Boolean(analysis.current_version) &&
    ("version" in analysis ? Boolean(analysis.version) : true)
  )
}

// The step a draft opens on when its step was not remembered.
export function derivedStep(
  analysis: AnalysisDetail | AnalysisRow
): WizardStep {
  if (analysis.status === "applied") return "review"
  if (!hasResult(analysis)) return "destination"
  if (!("version" in analysis) || !analysis.version) return "plan"
  return analysis.version.plan ? "review" : "plan"
}

// The old workspace's tabs, for links that still name one (`?tab=` on the retired
// /ai-center/sow-receivables/:id page) and for in-step jumps the tabs ask for.
export function stepForTab(tab: WorkspaceTab | string | null): {
  step: WizardStep
  panel?: WizardPanel
} | null {
  switch (tab) {
    case "overview":
    case "milestones":
    case "payments":
    case "subcontractors":
      return { step: "plan" }
    case "links":
    case "align":
    case "approve":
      return { step: "review" }
    case "template":
      return { step: "plan", panel: "template" }
    case "history":
      return { step: "plan", panel: "log" }
    default:
      return null
  }
}

export const isDepositLike = (r: ReceivableDraft) =>
  r.trigger_type === "on_signature" || DEPOSIT_WORDS.test(r.label)

const fromFinding = (finding: VersionDetail["validation"]["errors"][number]) =>
  ({
    id: `${finding.code}:${finding.key ?? finding.field ?? ""}`,
    code: finding.code,
    kind: "other",
    key: finding.key,
    field: finding.field,
    question: finding.message,
    explanation: "",
    values: {},
    recommended: null,
    citations: []
  }) satisfies Decision

/**
 * Every unresolved required decision, once each: the server decides what is one, and
 * every count on screen is the length of this list. A plan built for the record adds
 * what only the record can raise.
 */
export function openDecisions(
  version: VersionDetail,
  plan: AlignmentPlan | null = null
): Decision[] {
  const read = (validation: VersionDetail["validation"]) =>
    validation.decisions ?? validation.errors.map(fromFinding)
  const byId = new Map(read(version.validation).map((d) => [d.id, d]))
  if (plan) for (const d of read(plan.validation)) byId.set(d.id, d)
  for (const d of version.agreements?.decisions ?? []) byId.set(d.id, d)
  return [...byId.values()]
}

// A plan is current only when it was prepared from the draft as it is now: applying an
// older one would put the older plan on the record.
export const planIsCurrent = (
  analysis: Pick<AnalysisDetail, "revision">,
  plan: AlignmentPlan | null | undefined
) =>
  Boolean(plan) &&
  plan!.status !== "applied" &&
  plan!.built_revision === analysis.revision

export type StepState = "done" | "attention" | "todo" | "locked"

// What the step rail shows beside each step, read from the saved analysis.
export function stepStates(
  analysis: AnalysisDetail,
  decisions: number
): Record<WizardStep, StepState> {
  const ready = hasResult(analysis) && analysis.version
  if (!ready)
    return {
      destination: analysis.files.length ? "todo" : "attention",
      plan: "locked",
      review: "locked"
    }
  const applied = analysis.status === "applied"
  return {
    destination: "done",
    plan: applied ? "done" : decisions ? "attention" : "done",
    review: applied ? "done" : "todo"
  }
}
